import * as React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'context/auth-provider';
import DataGridPagination from 'components/data-grid-pagination';
import { getUtcDatetimeDisplayString } from 'utils/util';
import { getEventDetails, getEvents } from './events.api';
import { isEventSortField } from './events.types';
import type { EventDetails, EventSummary } from './events.types';
import EventDataDrawer from './event-data-drawer';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

export const parseEventSearch = (input: string): string[] => {
  const normalizedSearch = input.trim();
  return normalizedSearch.length >= MIN_SEARCH_LENGTH ? [normalizedSearch] : [];
};

const EventsToolbar = () => (
  <GridToolbarContainer sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
    <GridToolbarQuickFilter
      debounceMs={SEARCH_DEBOUNCE_MS}
      quickFilterParser={parseEventSearch}
      placeholder="Search events (3+ characters)"
      inputProps={{ 'aria-label': 'Search events' }}
    />
  </GridToolbarContainer>
);

const displayValue = (value?: string | null): string => value || '--';

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    return typeof responseMessage === 'string' && responseMessage
      ? responseMessage
      : error.message;
  }

  return error instanceof Error ? error.message : fallbackMessage;
};

export default function EventsView() {
  const theme = useTheme();
  const { user } = useAuth();
  const [events, setEvents] = React.useState<EventSummary[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<EventSummary | null>(null);
  const [eventDetails, setEventDetails] = React.useState<EventDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const [detailsRefreshSequence, setDetailsRefreshSequence] = React.useState(0);
  const [refreshSequence, setRefreshSequence] = React.useState(0);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const latestRequest = React.useRef(0);
  const latestDetailsRequest = React.useRef(0);
  const idTokenRef = React.useRef(user?.idToken);
  const searchQuery = filterModel.quickFilterValues?.[0]?.trim() || '';
  const selectedEventId = selectedEvent?.event_id;

  React.useEffect(() => {
    idTokenRef.current = user?.idToken;
  }, [user?.idToken]);

  const loadEvents = React.useCallback(async (signal: AbortSignal): Promise<void> => {
    const idToken = idTokenRef.current;
    if (!idToken) return;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setLoading(true);
    setError(null);

    const requestedSort = sortModel[0];
    const sortField = requestedSort && isEventSortField(requestedSort.field)
      ? requestedSort.field
      : undefined;

    try {
      const response = await getEvents({
        idToken,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: searchQuery || undefined,
        signal,
        sortField,
        sortDirection: sortField ? requestedSort?.sort || undefined : undefined,
      });

      if (signal.aborted || latestRequest.current !== requestId) return;

      setEvents(Array.isArray(response.items) ? response.items : []);
      setRowCount(Number.isFinite(response.totalItems) ? response.totalItems : 0);
    } catch (requestError: unknown) {
      if (
        signal.aborted ||
        axios.isCancel(requestError) ||
        latestRequest.current !== requestId
      ) {
        return;
      }

      setError(getErrorMessage(requestError, 'Failed to load events'));
    } finally {
      if (!signal.aborted && latestRequest.current === requestId) {
        setLoading(false);
      }
    }
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, sortModel]);

  const loadEventDetails = React.useCallback(async (
    eventId: string,
    signal: AbortSignal,
  ): Promise<void> => {
    const idToken = idTokenRef.current;
    if (!idToken) return;

    const requestId = latestDetailsRequest.current + 1;
    latestDetailsRequest.current = requestId;

    setDetailsLoading(true);
    setDetailsError(null);

    try {
      const details = await getEventDetails({
        eventId,
        idToken,
        signal,
      });

      if (signal.aborted || latestDetailsRequest.current !== requestId) return;

      setEventDetails(details);
    } catch (requestError: unknown) {
      if (
        signal.aborted ||
        axios.isCancel(requestError) ||
        latestDetailsRequest.current !== requestId
      ) {
        return;
      }

      setDetailsError(getErrorMessage(requestError, 'Failed to load event details'));
    } finally {
      if (!signal.aborted && latestDetailsRequest.current === requestId) {
        setDetailsLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    const abortController = new AbortController();
    void loadEvents(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [loadEvents, refreshSequence]);

  React.useEffect(() => {
    if (!selectedEventId) return;

    const abortController = new AbortController();
    void loadEventDetails(selectedEventId, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [detailsRefreshSequence, loadEventDetails, selectedEventId]);

  const handlePaginationModelChange = React.useCallback((nextModel: GridPaginationModel) => {
    setPaginationModel((currentModel) => ({
      page: nextModel.pageSize === currentModel.pageSize ? nextModel.page : 0,
      pageSize: nextModel.pageSize,
    }));
  }, []);

  const handleSortModelChange = React.useCallback((nextModel: GridSortModel) => {
    setSortModel(nextModel.slice(0, 1));
    setPaginationModel((currentModel) =>
      currentModel.page === 0 ? currentModel : { ...currentModel, page: 0 });
  }, []);

  const handleFilterModelChange = React.useCallback((nextModel: GridFilterModel) => {
    setFilterModel(nextModel);
    setPaginationModel((currentModel) =>
      currentModel.page === 0 ? currentModel : { ...currentModel, page: 0 });
  }, []);

  const handleEventSelection = React.useCallback((event: EventSummary) => {
    setSelectedEvent(event);
    setEventDetails(null);
    setDetailsError(null);
    setDetailsLoading(true);
    setDetailsRefreshSequence((sequence) => sequence + 1);
  }, []);

  const handleDetailsClose = React.useCallback(() => {
    setSelectedEvent(null);
    setEventDetails(null);
    setDetailsError(null);
    setDetailsLoading(false);
  }, []);

  const columns = React.useMemo<Array<GridColDef<EventSummary>>>(() => [
    {
      field: 'event_name',
      headerName: 'Event Name',
      minWidth: 180,
      flex: 1,
      sortable: true,
      pinnable: true,
      filterable: false,
    },
    {
      field: 'client_name',
      headerName: 'Client Name',
      minWidth: 180,
      flex: 1,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        displayValue(params.row.client_name),
    },
    {
      field: 'operation_status',
      headerName: 'Operation Status',
      minWidth: 140,
      flex: 0.75,
      sortable: true,
      pinnable: true,
      filterable: false,
    },
    {
      field: 'initiated_by',
      headerName: 'Initiated By',
      minWidth: 180,
      flex: 0.9,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        displayValue(params.row.initiated_by),
    },
    {
      field: 'created_at',
      headerName: 'Created At (UTC)',
      minWidth: 210,
      flex: 0.9,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        getUtcDatetimeDisplayString(params.row.created_at),
    },
  ], []);

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography component="h2" variant="h6" color="primary" sx={{ flexGrow: 1 }}>
            Events
          </Typography>
          <Tooltip title="Refresh events">
            <span>
              <IconButton
                aria-label="Refresh events"
                disabled={loading}
                onClick={() => setRefreshSequence((sequence) => sequence + 1)}
                sx={{ color: theme.palette.primary.main }}
              >
                <CachedIcon
                  sx={{
                    animation: loading ? 'spin 1s linear infinite' : 'none',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <IconButton
                aria-label="Retry loading events"
                color="inherit"
                size="small"
                onClick={() => setRefreshSequence((sequence) => sequence + 1)}
              >
                <CachedIcon fontSize="inherit" />
              </IconButton>
            )}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 300, flexGrow: 1 }}>
          <DataGridPro
            rows={events}
            columns={columns}
            getRowId={(row) => row.event_id}
            onRowClick={(params) => handleEventSelection(params.row)}
            onCellKeyDown={(params, event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleEventSelection(params.row);
              }
            }}
            loading={loading}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            rowCount={rowCount}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableMultipleColumnsSorting
            disableRowSelectionOnClick
            slots={{
              toolbar: EventsToolbar,
              pagination: DataGridPagination,
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
            }
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
              '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[100],
              },
              '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[300],
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      <EventDataDrawer
        details={eventDetails}
        error={detailsError}
        event={selectedEvent}
        loading={detailsLoading}
        onClose={handleDetailsClose}
        onRetry={() => setDetailsRefreshSequence((sequence) => sequence + 1)}
      />
    </Box>
  );
}
